import { useEffect, useRef, useState } from 'react';
import { API_BASE, currentAccessToken } from './client';

/**
 * Поток от сървъра. Не се ползва `EventSource`, защото той не носи заглавка
 * `Authorization`, а токенът в адреса влиза в дневниците на всеки посредник.
 * Затова потокът се чете с `fetch` и ReadableStream.
 *
 * След три неуспешни опита състоянието става `polling` — бордът превключва на
 * обикновено допитване на 20 секунди и продължава да работи. Диспечерът вижда
 * състоянието на връзката винаги, защото „борд, който е замръзнал без да каже"
 * е по-опасен от липсващ борд.
 */
export type StreamStatus = 'connecting' | 'live' | 'reconnecting' | 'polling';

export interface StreamMessage {
  event: string;
  data: string;
}

const MAX_ATTEMPTS_BEFORE_POLLING = 3;

export function useEventStream(
  path: string,
  options: { enabled?: boolean; onMessage: (message: StreamMessage) => void },
): { status: StreamStatus; lastMessageAt: number | null } {
  const { enabled = true, onMessage } = options;
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const [lastMessageAt, setLastMessageAt] = useState<number | null>(null);
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let attempt = 0;
    let timer: number | null = null;
    const controller = new AbortController();

    const parseFrames = (chunk: string): StreamMessage[] => {
      const frames = chunk.split('\n\n').filter((f) => f.trim().length > 0);
      const messages: StreamMessage[] = [];
      for (const frame of frames) {
        let event = 'message';
        const dataLines: string[] = [];
        for (const line of frame.split('\n')) {
          if (line.startsWith(':')) continue;
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        if (dataLines.length > 0) messages.push({ event, data: dataLines.join('\n') });
      }
      return messages;
    };

    const connect = async (): Promise<void> => {
      if (cancelled) return;
      setStatus(attempt === 0 ? 'connecting' : attempt >= MAX_ATTEMPTS_BEFORE_POLLING ? 'polling' : 'reconnecting');

      try {
        const token = await currentAccessToken();
        const headers: Record<string, string> = { accept: 'text/event-stream' };
        if (token) headers.authorization = `Bearer ${token}`;

        const response = await fetch(`${API_BASE}${path}`, {
          headers,
          signal: controller.signal,
        });
        if (!response.ok || !response.body) throw new Error(`stream ${response.status}`);

        attempt = 0;
        if (!cancelled) setStatus('live');

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done || cancelled) break;
          buffer += decoder.decode(value, { stream: true });
          const lastBreak = buffer.lastIndexOf('\n\n');
          if (lastBreak === -1) continue;
          const complete = buffer.slice(0, lastBreak);
          buffer = buffer.slice(lastBreak + 2);
          for (const message of parseFrames(complete)) {
            handlerRef.current(message);
            setLastMessageAt(Date.now());
          }
        }
        throw new Error('stream closed');
      } catch (error) {
        if (cancelled || controller.signal.aborted) return;
        attempt += 1;
        setStatus(attempt >= MAX_ATTEMPTS_BEFORE_POLLING ? 'polling' : 'reconnecting');
        const delayMs = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5));
        timer = window.setTimeout(() => void connect(), delayMs);
      }
    };

    void connect();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [path, enabled]);

  return { status, lastMessageAt };
}
